import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { NAV_LINKS } from "../../lib/siteData";
import { Menu, X } from "lucide-react";

const VERT_COUNT = 24;

export const SpineRail = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? h.scrollTop / max : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const lit = Math.round(progress * VERT_COUNT);
  return (
    <div className="spine-rail" data-testid="spine-rail" aria-hidden="true">
      {Array.from({ length: VERT_COUNT }).map((_, i) => (
        <span key={i} className={`vertebra ${i < lit ? "lit" : ""}`} />
      ))}
    </div>
  );
};

export const Nav = () => {
  const location = useLocation();
  const homePrefix = location.pathname === "/" ? "" : "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#F3F5F4]/90 backdrop-blur-md border-b border-[#DCE3E0]" : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
        <a href={`${homePrefix}#top`} className="flex flex-col leading-none" data-testid="nav-brand">
          <span className={`font-display text-lg md:text-xl font-semibold tracking-tight transition-colors ${scrolled ? "text-[#1F3864]" : "text-white"}`}>
            Dr. Amit Chanduka
          </span>
          <span className="eyebrow text-[#5ECCA0] mt-1 text-[0.6rem]">Neurosurgeon &amp; Spine Surgeon</span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={`${homePrefix}${l.href}`}
              data-scroll-offset={l.offset || -80}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#5ECCA0] hover:after:w-full after:transition-all after:duration-300 ${scrolled ? "text-[#161C22]/80 hover:text-[#1F3864]" : "text-white/80 hover:text-[#5ECCA0]"}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`${homePrefix}#contact`}
            data-testid="nav-book-btn"
            className={`hidden sm:inline-flex items-center rounded-full text-sm px-5 py-2.5 hover-lift transition-colors ${scrolled ? "bg-[#1F3864] text-white hover:shadow-lg hover:shadow-[#1F3864]/20" : "bg-[#5ECCA0] text-[#14263D] hover:shadow-lg hover:shadow-[#5ECCA0]/25"}`}
          >
            Book Consultation
          </a>
          <button
            className={`lg:hidden p-2 transition-colors ${scrolled ? "text-[#1F3864]" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F3F5F4] border-t border-[#DCE3E0] px-5 py-4" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={`${homePrefix}${l.href}`}
                data-scroll-offset={l.offset || -80}
                onClick={() => setOpen(false)}
                className="py-2.5 text-[#161C22] border-b border-[#DCE3E0]/60 last:border-0"
              >
                {l.label}
              </a>
            ))}
            <a href={`${homePrefix}#contact`} onClick={() => setOpen(false)} className="mt-3 text-center rounded-full bg-[#1F3864] text-white py-3">
              Book Consultation
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const location = useLocation();
  const homePrefix = location.pathname === "/" ? "" : "/";
  return (
    <footer className="bg-[#14263D] text-[#F3F5F4]/70" data-testid="site-footer">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 py-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="font-display text-2xl text-white font-semibold">Dr. Amit Chanduka</div>
            <div className="eyebrow text-[#5ECCA0] mt-2">MCh Neurosurgery · Bhubaneswar, Odisha</div>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            {NAV_LINKS.concat([{ label: "Contact", href: "#contact" }]).map((l) => (
              <a key={l.href} href={`${homePrefix}${l.href}`} className="text-sm hover:text-[#5ECCA0] transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs font-mono-ui text-[#F3F5F4]/45">
          <span>© {new Date().getFullYear()} Dr. Amit Chanduka. All rights reserved.</span>
          <span>For information and patient education only — not a substitute for medical advice.</span>
        </div>
      </div>
    </footer>
  );
};
